import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { Layers, Film } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard } from "@/components/layout/locked-content";

import { cn, message } from "@/lib/utils";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";
import type { CreativeAuditAPIData } from "@/schema/brand-os/brand-intel";

interface StatCard {
  label: string;
  value: string;
  subLabel: string;
  Icon: LucideIcon;
}

function buildCards(d: CreativeAuditAPIData): StatCard[] {
  const cards: StatCard[] = [];
  if (d.total_active_ads > 0) {
    cards.push({
      label: "Total Active Ads",
      value: String(d.total_active_ads),
      subLabel: `Across ${d.platform_distribution.length} platform${d.platform_distribution.length !== 1 ? "s" : ""}`,
      Icon: Layers,
    });
  }
  if (d.video_count > 0 || d.static_count > 0) {
    cards.push({
      label: "Video : Static",
      value: `${d.video_count}:${d.static_count}`,
      subLabel: `${d.video_count} video, ${d.static_count} static`,
      Icon: Film,
    });
  }
  return cards;
}

function CreativeCardsGrid({ cards }: { cards: StatCard[] }) {
  if (!cards.length) return <LockedCard message={message} />;
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => (
        <Card key={card.label}>
          <CardContent className="space-y-3">
            <div className={cn("flex size-8 items-center justify-center rounded-lg bg-muted")}>
              <card.Icon className="size-4 text-foreground/70" strokeWidth={2.5} />
            </div>
            <p className="text-3xl font-bold text-foreground">{card.value}</p>
            <div>
              <p className="text-sm text-muted-foreground">{card.label}</p>
              <p className="text-xs text-muted-foreground/60">{card.subLabel}</p>
            </div>
          </CardContent>
        </Card>
      ))}
      <LockedCard message={message} />
      <LockedCard message={message} />
    </div>
  );
}

function CardsContent({ status, cards }: { status: QueryStatus; cards: StatCard[] }) {
  switch (status) {
    case "pending":
      return <StatCardsSkeleton />;
    case "error":
      return <LockedCard message={message} />;
    case "success":
      return <CreativeCardsGrid cards={cards} />;
  }
}

export function AuditStatCards() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchCreativeAudit());
  const cards = data ? buildCards(data) : [];

  return <CardsContent status={status} cards={cards} />;
}
function StatCardsSkeleton() {
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }, (_, i) => (
        <Card key={i}>
          <CardContent className="space-y-3">
            <Skeleton className="size-8 rounded-lg" />
            <Skeleton className="h-9 w-20" />
            <div className="space-y-1">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-32" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
